import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { t } from './i18n';
import { useIsMobile } from './hooks/useIsMobile';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { AuthGate } from './components/AuthGate';
import { AppNav } from './components/AppNav';
import { BurgerMenu } from './components/BurgerMenu';
import { VerifyEmailBanner } from './components/VerifyEmailBanner';
import { CliAuthPage } from './components/CliAuthPage';
import { NotificationsPermissionBanner } from './components/NotificationsPermissionBanner';
import { NotificationsProvider } from './components/NotificationsProvider';
import { DMView } from './components/DMView';
import { SpaceView } from './components/SpaceView';
import {
  SelectedSpaceProvider,
  useSelectedSpace,
} from './components/SelectedSpaceContext';
import {
  SelectedDMProvider,
  useSelectedDM,
} from './components/SelectedDMContext';
import { SettingsDrawer } from './components/SettingsDrawer';
import {
  SettingsDrawerProvider,
  useSettingsDrawer,
} from './components/SettingsDrawerContext';
import { ForgotPasswordForm } from './components/ForgotPasswordForm';
import { NotFoundPage } from './components/NotFoundPage';
import { ResetPasswordForm } from './components/ResetPasswordForm';
import { Sidebar } from './components/Sidebar';
import { VerifyEmailHandler } from './components/VerifyEmailHandler';
import { useFirstRun } from './components/concierge/firstRun';
import { shouldLandOnOrg } from './lib/orgLanding';

const PricingPage = lazy(() =>
  import('./components/PricingPage').then((m) => ({
    default: m.PricingPage,
  })),
);

// The Concierge Thread is a first-run-only surface (empty account, once). Lazy
// so it — and its deps (health hook, staged reveal, Scout content) — stay OUT of
// the main entry bundle; returning users, the vast majority of loads, never
// download it. `useFirstRun` (the cheap detection) stays in the entry bundle.
const ConciergeFirstRun = lazy(() =>
  import('./components/concierge/ConciergeFirstRun').then((m) => ({
    default: m.ConciergeFirstRun,
  })),
);

// Converged mesh operator-console (the HI/UX shell). Lazy + namespaced so it
// adds nothing to the entry bundle and its theme can't disturb the chat console.
const MeshConsolePage = lazy(() =>
  import('./components/mesh/MeshConsolePage').then((m) => ({
    default: m.MeshConsolePage,
  })),
);

// ORG-BUILDER v1 — the CLEAN Org page (Track P, Erick decision c). A focused route
// that mounts ONLY the v1 surface (editable canvas + per-agent inspector + Start +
// "Talk to the swarm"), reusing the same canvas/inspector components as the dev
// console without its Simulate/Harden/Timeline/refine chrome. Lazy + namespaced
// like the mesh shell so it adds nothing to the entry bundle and keeps the dark
// theme self-contained. The existing /app + /mesh dev console stay intact.
const OrgBuilderPage = lazy(() =>
  import('./components/mesh/OrgBuilderPage').then((m) => ({
    default: m.OrgBuilderPage,
  })),
);

// Converged-console Onboarding surface (download → sign-in → first machine
// online → create a project → place an agent) — the DESKTOP install stepper, and
// nothing to do with the signup-time `/onboarding` fork T-0175 retired. It mounts
// under the `/mesh` namespace. Lazy + namespaced like the mesh shell so it adds
// nothing to the entry bundle and keeps its dark theme self-contained.
// T-0167 — the fleet Schedules view (altitude 3). Lazy: an operator who never opens
// it never downloads it, and it drags in nothing the entry bundle already has.
const FleetSchedulesPage = lazy(() =>
  import('./components/schedules/FleetSchedulesPage').then((m) => ({
    default: m.FleetSchedulesPage,
  })),
);

const MeshOnboardingPage = lazy(() =>
  import('./components/onboarding/MeshOnboardingPage').then((m) => ({
    default: m.MeshOnboardingPage,
  })),
);

// `/messages` is the canonical Messages route — it renders the same authenticated
// messaging Shell as '/', but is NOT subject to the canvas-first landing redirect
// (which only fires on a bare '/'). This gives the persistent nav a Messages
// destination that a mesh-owner can actually reach (bare '/' bounces them to /org).
// T-0221 (packet 007, D21) — `/settings` is a real, deep-linkable route. It falls through
// to the authenticated Shell branch (below), where the SettingsDrawerProvider seeds the
// drawer open from the URL — so a direct load / a refresh of `/settings` paints the drawer,
// and the rail gear + mobile tab navigate here (AppNav) instead of 404ing.
const KNOWN_EXACT_PATHS = new Set(['/', '/messages', '/settings', '/login', '/signup']);
const KNOWN_PATH_PREFIXES = ['/verify/', '/reset/', '/forgot-password'];
const SIDEBAR_COLLAPSE_QUERY = '(max-width: 768px)';

function isKnownPath(pathname: string): boolean {
  if (KNOWN_EXACT_PATHS.has(pathname)) return true;
  return KNOWN_PATH_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * A path we no longer serve, forwarded to the one that replaced it.
 *
 * `replace`, not `assign`: the retired page must not sit in history, or Back from the
 * canvas bounces the user straight through it again. The redirect runs in an effect
 * rather than during render — a navigation mid-render is a side effect React is free to
 * run twice — and until it fires we paint the same "Loading…" the canvas-first landing
 * paints, so the two look like one journey rather than a flash of something retired.
 */
function RetiredRoute({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return (
    <div className="auth-loading" role="status" aria-label="Loading">
      Loading…
    </div>
  );
}

function shouldCollapseSidebarForViewport(): boolean {
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia(SIDEBAR_COLLAPSE_QUERY).matches;
  }
  return window.innerWidth <= 768;
}

export function App() {
  // Lightweight path-based routing: a handful of unauthenticated pages
  // (`/verify/<token>`, `/forgot-password`, `/reset/<token>`, `/pricing`)
  // bypass AuthGate. Known shell paths (`/`, `/login`, `/signup`) fall
  // through to AuthGate. Anything else renders the 404 page. When the app
  // grows past a handful of paths, swap in React Router.
  const pathname = window.location.pathname;
  if (pathname.startsWith('/verify/')) {
    return <VerifyEmailHandler />;
  }
  if (pathname.startsWith('/forgot-password')) {
    return (
      <div className="auth-signed-out">
        <ForgotPasswordForm />
      </div>
    );
  }
  if (pathname.startsWith('/reset/')) {
    return (
      <div className="auth-signed-out">
        <ResetPasswordForm />
      </div>
    );
  }
  // T-0175 — `/onboarding` was the "Start your operator console" fork (Connect agent |
  // Create space | Open console): the last non-canvas first screen, and the one Erick
  // retired outright. The page is gone; the path is not, because it is in the wild —
  // signup used to hard-navigate here, and a stale tab or a bookmark still can. It
  // forwards to the canvas rather than 404ing at someone whose only crime was
  // returning. Both of its capabilities live on in Settings (⚙ in the rail), which is
  // now reachable from the canvas — see `AppNav`.
  if (pathname === '/onboarding') {
    return <RetiredRoute to="/org" />;
  }
  // MSW-only route: triggers the ErrorBoundary for E2E verification.
  // Playwright uses `VITE_MSW=1 vite build` (not `vite dev`), so
  // import.meta.env.DEV is false — guard by VITE_MSW instead, which is
  // true in both dev and Playwright builds and false in real prod builds.
  if (pathname === '/test/error-boundary' && import.meta.env.VITE_MSW) {
    throw new Error('test-render-error: ErrorBoundary E2E trigger');
  }
  if (pathname === '/pricing') {
    return (
      <Suspense fallback={<LazyFallback />}>
        <PricingPage />
      </Suspense>
    );
  }
  // The converged mesh operator-console shell (HI/UX) — the ORG-BUILDER surface.
  //
  // `/app` is the STABLE staging-home path the server serves the SPA at (M1
  // visitable-staging gate): tizokd's static serve returns this same index.html
  // for `/app`, and here `/app` renders the mesh console — so the one URL anyone
  // opens, https://<host>/app, lands directly on the mesh. As of ORG-BUILDER v1
  // (Track C) `/app` is behind AuthGate: a signed-out operator gets the inline
  // LoginForm, and the builder — whose write surface (create/save/agent CRUD) is
  // owner-scoped on the real plane — renders only once the session check passes
  // (in dev the default MSW `GET /api/me` returns the mock operator, so the console
  // still renders backend-free). `/mesh` deliberately stays UNAUTHENTICATED: it is
  // the pre-existing scaffold route the E2E harness drives, including the hermetic
  // capstone against the run-engine, which mounts only the mesh-API (NO `/api/me`)
  // — gating it would wedge that suite behind a session the backend cannot serve.
  // ORG-BUILDER v1 — the CLEAN Org page. The focused, ship-it-v1 surface: build a
  // swarm by hand, set each agent's job/model/behavior_protocol, Start it, and Talk
  // to it in messaging. Behind AuthGate like /app (its write surface is owner-scoped
  // on the real plane; in dev the default MSW `GET /api/me` returns the mock operator
  // so it still renders backend-free). The dev console at /app + /mesh is untouched.
  if (pathname === '/org') {
    // NotificationsProvider wraps the page because the inspector's Chat tab (stage 3 —
    // talk to your agents) reuses DMView, whose MessageThread reads the notifications
    // context (mention alerts / mute). QueryClientProvider is already supplied at the app
    // root (main.tsx), so the Chat tab's DM resolve + thread queries work here.
    return (
      <AuthGate>
        {/* T-0175 — the canvas is the first screen, so Settings has to be reachable FROM
            it. This surface has no app header (no BurgerMenu), so before this the drawer
            was unreachable here and a brand-new account could never open Agents or
            Spaces at all. The rail's ⚙ opens it; the provider has to sit above the rail. */}
        <SettingsDrawerProvider>
          <Suspense fallback={<LazyFallback />}>
            <NotificationsProvider>
              <OrgBuilderPage />
            </NotificationsProvider>
          </Suspense>
          {/* Rendered AFTER the surface so the surface's own skip-link/controls keep
              their focus order; the rail is position:fixed so it still shows on the left. */}
          <AppNav />
          <SettingsDrawer />
        </SettingsDrawerProvider>
      </AuthGate>
    );
  }
  // T-0167 — the fleet plane. Behind AuthGate like /org (the rows are owner-scoped
  // server-side), with the rail rendered after the surface so its skip-link keeps
  // focus order. `startsWith` so a later `/schedules/…` detail route lands here too.
  if (pathname.startsWith('/schedules')) {
    return (
      <AuthGate>
        <SettingsDrawerProvider>
          <Suspense fallback={<LazyFallback />}>
            <FleetSchedulesPage />
          </Suspense>
          <AppNav />
          <SettingsDrawer />
        </SettingsDrawerProvider>
      </AuthGate>
    );
  }
  if (pathname === '/app') {
    return (
      <AuthGate>
        <Suspense fallback={<LazyFallback />}>
          <MeshConsolePage />
        </Suspense>
      </AuthGate>
    );
  }
  if (pathname === '/mesh') {
    return (
      <Suspense fallback={<LazyFallback />}>
        <MeshConsolePage />
      </Suspense>
    );
  }
  // The converged-console Onboarding surface: the DESKTOP install stepper (download →
  // sign in → first machine online). It survives T-0175 — retiring the signup fork did
  // not retire the install funnel, and this route is the only thing that renders it.
  // Unauthenticated for the M0 scaffold so it renders + is E2E-testable; AuthGate wraps
  // the signed-in scenes in M1+.
  if (pathname === '/mesh/onboarding') {
    return (
      <Suspense fallback={<LazyFallback />}>
        <MeshOnboardingPage />
      </Suspense>
    );
  }
  if (pathname === '/cli-auth') {
    // CLI OAuth-style flow per CLI_DESIGN_NOTES.md `tizok login`. Backend
    // gates `/cli-auth` behind a web session — signed-out operators get
    // the existing LoginForm via AuthGate, then come back here once auth
    // completes (backend's `Set-Cookie` + this page's same-origin fetch).
    return (
      <AuthGate>
        <CliAuthPage />
      </AuthGate>
    );
  }
  if (
    !isKnownPath(pathname) &&
    pathname !== '/pricing' &&
    pathname !== '/cli-auth'
  ) {
    return <NotFoundPage />;
  }
  return (
    <AuthGate>
      <NotificationsProvider>
        <SettingsDrawerProvider>
          <SelectedSpaceProvider>
            <SelectedDMProvider>
              <Shell />
            </SelectedDMProvider>
          </SelectedSpaceProvider>
          {/* Rendered AFTER the Shell so the skip-link stays the first focusable
              element; the rail is position:fixed so it still shows on the left.
              INSIDE the drawer provider (T-0175) because the rail's ⚙ opens it — and
              on the first-run Concierge, which suppresses the app header, that ⚙ is
              the only way in. `Shell` renders the drawer itself on this branch. */}
          <AppNav />
        </SettingsDrawerProvider>
      </NotificationsProvider>
    </AuthGate>
  );
}

// Whether the canvas-first landing redirect may auto-fire in THIS build. Always true
// in the real app; in the MSW e2e harness (`VITE_MSW`) it is OFF unless a spec opts in
// with the `e2e_land` flag — so the messaging-console specs (bare "/") aren't bounced
// to /org, while landing-redirect.spec.ts still proves the redirect. Kept out of the
// pure shouldLandOnOrg (which the effect still evaluates) because it reads the env +
// localStorage. Folds to `true` under dead-code elimination in prod (VITE_MSW inlined).
function landingEnabledInHarness(): boolean {
  if (!import.meta.env.VITE_MSW) return true;
  try {
    return localStorage.getItem('e2e_land') === '1';
  } catch {
    return false;
  }
}

function Shell() {
  // The Concierge Thread — the two-pane chat shell pre-seeded with one waiting Scout
  // conversation — is a first-run surface of THIS shell. It is no longer the landing
  // (T-0162): a bare "/" now takes a brand-new account to the canvas. The Concierge is
  // where the rail's ✉ Messages (`/messages`, exempt from the redirect) puts them, so
  // the chat window stays — reachable, just not the front door.
  const { isFirstRun, resolved } = useFirstRun();

  // ── Canvas-first landing (ORG-BUILDER · T-0162) ───────────────────────────────
  // The canvas is the home: a bare "/" lands a signed-in operator on /org, first run
  // included. `isFirstRun` used to bail here because a new account owned no mesh and
  // there was "nothing to land on"; `/org` now has a real zero-mesh surface ("No agents
  // yet" → ＋Add agent auto-drafts the swarm), so first-run GRANTS the landing instead.
  // A returning operator still needs to own an org; a messaging deep-link (`/?space=…`)
  // is left alone. Gated OFF under Vitest (jsdom cannot navigate; the console shell
  // tests assert "/" renders messaging) — the real app runs it; the DECISION is
  // unit-tested via shouldLandOnOrg.
  //
  // Gated OFF the same way in the MSW e2e harness (`VITE_MSW` build) by default: the
  // seeded meshes are FIXTURES for the /mesh + /org specs, not a signal that THIS
  // operator asked for canvas-first landing, and the dozens of console e2e specs load a
  // bare "/" to assert the messaging shell. A spec opts INTO the redirect with the
  // `e2e_land` flag (see landing-redirect.spec.ts), so the wiring is still proven
  // end-to-end. Real prod never sets VITE_MSW, so it always lands per shouldLandOnOrg.
  //
  // The decision reads the ENTRY location (frozen at mount), NOT live window.location:
  // a `/?space=…` deep link is stripped to a bare "/" on mount by SelectedSpaceProvider
  // (history.replaceState, so React never re-renders on it). Reading live would let the
  // effect re-fire against the now-bare "/" once the account queries resolve — bouncing
  // a messaging deep link to /org. Freezing the entry search keeps deep links untouched.
  const [entry] = useState(() => ({
    pathname: window.location.pathname,
    search: window.location.search,
  }));
  const canLand =
    import.meta.env.MODE !== 'test' &&
    landingEnabledInHarness() &&
    resolved &&
    entry.pathname === '/' &&
    !entry.search;
  // Only the mesh COUNT decides the redirect, so the mesh api client is pulled in
  // lazily (a dynamic import) instead of statically: importing useOperatorMeshes here
  // would drag the mesh api client + the canvas graph adapter (lib/mesh-graph) into
  // the entry chunk for every first load, landing or not. The client + canvas pages
  // stay code-split. `null` = list not yet resolved (don't decide until it is).
  const [landingMeshCount, setLandingMeshCount] = useState<number | null>(null);
  useEffect(() => {
    // A first-run account lands on its empty canvas regardless of the count, so it
    // never pays for this round-trip — the one load where latency is most visible.
    if (!canLand || isFirstRun) return;
    let cancelled = false;
    import('./mesh/api/client')
      .then(({ listMeshes }) => listMeshes())
      .then((ms) => {
        if (!cancelled) setLandingMeshCount(ms.length);
      })
      .catch(() => {
        if (!cancelled) setLandingMeshCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [canLand, isFirstRun]);

  // `null` ⇒ the count is still outstanding, so no decision yet. A first-run account
  // has no count to wait for: zero meshes is exactly what it owns.
  const meshCountForLanding = isFirstRun ? 0 : landingMeshCount;
  const landing =
    canLand &&
    meshCountForLanding !== null &&
    shouldLandOnOrg({
      resolved,
      isFirstRun,
      pathname: entry.pathname,
      search: entry.search,
      meshCount: meshCountForLanding,
    });

  // The Concierge is a first-run surface of the messaging Shell — reached through the
  // rail's ✉ Messages (`/messages`), which this redirect never touches. It must NOT
  // paint on the way out of a bare "/", or a brand-new operator sees a flash of the
  // very chat window we are taking them away from.
  const conciergeFirstRun = resolved && isFirstRun && !landing;
  useEffect(() => {
    if (landing) window.location.replace('/org');
  }, [landing]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    shouldCollapseSidebarForViewport,
  );
  const {
    isOpen: settingsOpen,
    open: openSettings,
    close: closeSettings,
  } = useSettingsDrawer();

  // MOBILE master-detail (T-0200 mobile rider / packet 005): the phone is a
  // DRILL-DOWN. With nothing selected it shows the space LIST (not an empty detail
  // telling you to "Show spaces"); selecting a conversation opens it full-screen,
  // and a back chevron returns to the list. Desktop keeps both panes side by side.
  const isMobile = useIsMobile();
  const { selectedSpaceId, setSelectedSpaceId } = useSelectedSpace();
  const { selectedDmId, setSelectedDmId } = useSelectedDM();
  const hasSelection = Boolean(selectedSpaceId || selectedDmId);
  const clearSelection = useCallback(() => {
    setSelectedSpaceId(null);
    setSelectedDmId(null);
  }, [setSelectedSpaceId, setSelectedDmId]);
  // Desktop-only: picking a conversation on a narrow desktop window collapses the
  // sidebar so the thread owns the width (the mobile branch renders one pane at a
  // time instead, so this is a no-op there).
  useEffect(() => {
    if (
      !isMobile &&
      (selectedSpaceId || selectedDmId) &&
      shouldCollapseSidebarForViewport()
    ) {
      setSidebarCollapsed(true);
    }
  }, [isMobile, selectedSpaceId, selectedDmId]);

  useKeyboardShortcuts({
    onSettings: useCallback(() => {
      if (settingsOpen) closeSettings();
      else openSettings();
    }, [settingsOpen, openSettings, closeSettings]),
    onEscape: useCallback(() => {
      if (settingsOpen) closeSettings();
    }, [settingsOpen, closeSettings]),
  });

  useEffect(() => {
    function syncSidebarToViewport() {
      setSidebarCollapsed(shouldCollapseSidebarForViewport());
    }

    const media = window.matchMedia?.(SIDEBAR_COLLAPSE_QUERY);
    syncSidebarToViewport();
    window.addEventListener('resize', syncSidebarToViewport);
    media?.addEventListener?.('change', syncSidebarToViewport);

    return () => {
      window.removeEventListener('resize', syncSidebarToViewport);
      media?.removeEventListener?.('change', syncSidebarToViewport);
    };
  }, []);

  return (
    <div className="app-shell">
      <a className="app-skip-link" href="#app-main-content">
        Skip to main content
      </a>
      <VerifyEmailBanner />
      {/* No ops-console chrome at first run, and none while we are on our way to the
          canvas — a header that paints for one frame and vanishes reads as a bug. */}
      {!conciergeFirstRun && !landing && (
        <header className="app-header" role="banner">
          <BurgerMenu />
          {isMobile ? (
            // Drill-down: a back chevron out of an open conversation, shown only
            // when there's something to go back FROM. The list is the phone's
            // home, so it needs no "Show spaces" toggle.
            hasSelection && (
              <button
                type="button"
                className="app-sidebar-toggle app-back-button"
                onClick={clearSelection}
                aria-label="Back to spaces"
              >
                ‹ Spaces
              </button>
            )
          ) : (
            <button
              type="button"
              className="app-sidebar-toggle"
              onClick={() => setSidebarCollapsed((v) => !v)}
              aria-pressed={sidebarCollapsed}
              aria-label={
                sidebarCollapsed ? 'Show spaces sidebar' : 'Hide spaces sidebar'
              }
            >
              {sidebarCollapsed ? 'Show spaces' : 'Hide spaces'}
            </button>
          )}
          {/* One name for the surface — the rail's word. "Operator Console" was
              dev register; the version string moved to Settings ▸ About (T-0200). */}
          <h1>{t.shell.appTitle}</h1>
        </header>
      )}
      <NotificationsPermissionBanner />
      <div className="app-body">
        {!resolved || landing ? (
          // `landing` ⇒ `window.location.replace('/org')` is already queued. Show the
          // same quiet Loading the unresolved state shows rather than flashing the
          // messaging console (or the Concierge) we are navigating away from.
          <div className="auth-loading" role="status" aria-label="Loading">
            Loading…
          </div>
        ) : conciergeFirstRun ? (
          <Suspense
            fallback={
              <div className="auth-loading" role="status" aria-label="Loading">
                Loading…
              </div>
            }
          >
            <ConciergeFirstRun />
          </Suspense>
        ) : isMobile ? (
          // One pane at a time: the list until you drill in, the thread after.
          hasSelection ? (
            <MainPane />
          ) : (
            <div className="app-body-mobile-list">
              <Sidebar collapsed={false} />
            </div>
          )
        ) : (
          <>
            <Sidebar collapsed={sidebarCollapsed} />
            <MainPane />
          </>
        )}
      </div>
      <SettingsDrawer />
    </div>
  );
}

function MainPane() {
  const { selectedSpaceId } = useSelectedSpace();
  const { selectedDmId } = useSelectedDM();

  if (selectedDmId) {
    return (
      <main id="app-main-content" className="app-main" tabIndex={-1}>
        <DMView dmId={selectedDmId} />
      </main>
    );
  }

  if (selectedSpaceId) {
    return (
      <main id="app-main-content" className="app-main" tabIndex={-1}>
        <SpaceView roomId={selectedSpaceId} />
      </main>
    );
  }

  return (
    <main
      id="app-main-content"
      className="app-main app-main-empty"
      tabIndex={-1}
    >
      <div className="empty-state" role="status">
        <span className="empty-state-icon" aria-hidden="true">
          👈
        </span>
        <p className="empty-state-title">Pick a space to get started</p>
        <p className="empty-state-description">
          Open a space from the sidebar. Invite collaborators from an active
          space when you're ready to start.
        </p>
      </div>
    </main>
  );
}

function LazyFallback() {
  return (
    <div className="auth-loading" role="status" aria-label="Loading page">
      Loading…
    </div>
  );
}
